import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Search } from "lucide-react";
import { cn } from "@nexus/ui";
import { AuditTimeline, type AuditTimelineSummary } from "./AuditTimeline";

const SEARCH_CLASS =
  "h-9 w-full rounded-md border border-input-border bg-background pl-9 pr-3 text-[13px] outline-none placeholder:text-muted-foreground/70 focus:border-ring focus:ring-1 focus:ring-ring/40";

type AuditCategory = "all" | "access" | "members" | "settings";

const CATEGORY_ACTION_PREFIX: Record<Exclude<AuditCategory, "all">, string[]> = {
  access: ["role_", "perm_", "invite_"],
  members: ["role_assigned", "role_revoked", "role_replaced"],
  settings: ["org_", "tenant_"],
};

export type OrgDrawerAuditTabProps = {
  tenantId: string;
  hasPermission: boolean;
  onEntryClick?: (entryId: string) => void;
  onSummaryChange?: (summary: AuditTimelineSummary) => void;
};

export function OrgDrawerAuditTab({ tenantId, hasPermission, onEntryClick, onSummaryChange }: OrgDrawerAuditTabProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState<AuditCategory>("all");

  const searchPrefill = useMemo(() => search.trim() || undefined, [search]);

  const categoryLabel = (id: AuditCategory) =>
    t(`iam.orgDrawer.auditTab.category.${id}`, {
      defaultValue:
        id === "all" ? "All" : id === "access" ? "Access" : id === "members" ? "Members" : "Settings",
    });

  return (
    <div data-testid="org-drawer-audit-body" className="flex flex-1 flex-col gap-4 px-6 py-5">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[200px] flex-1">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t(
              "iam.orgDrawer.auditTab.searchPlaceholder",
              "Search by person, role or setting…",
            )}
            className={SEARCH_CLASS}
            data-testid="org-drawer-audit-search"
          />
        </div>

        <div
          className="flex h-9 gap-0.5 rounded-md border border-border bg-muted p-0.5"
          role="group"
          aria-label={t("iam.orgDrawer.auditTab.categoryGroupAria", "Filter audit events")}
        >
          {(["all", "access", "members", "settings"] as const).map((id) => (
            <button
              key={id}
              type="button"
              data-testid={`org-drawer-audit-category-${id}`}
              aria-pressed={category === id}
              onClick={() => setCategory(id)}
              className={cn(
                "rounded-sm border px-2.5 text-[11px] font-medium transition-colors",
                category === id
                  ? "border-border bg-background text-primary shadow-sm"
                  : "border-transparent text-foreground hover:bg-background/60",
              )}
            >
              {categoryLabel(id)}
            </button>
          ))}
        </div>
      </div>

      <AuditTimeline
        scope="tenant"
        scopeId={tenantId}
        hasPermission={hasPermission}
        onEntryClick={onEntryClick}
        searchPrefill={searchPrefill}
        categoryFilter={category === "all" ? undefined : CATEGORY_ACTION_PREFIX[category]}
        variant="grouped"
        onSummaryChange={onSummaryChange}
      />
    </div>
  );
}
